'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Header } from '@/components/landing/header'
import { Footer } from '@/components/landing/footer'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Mail, Building2, ArrowRight } from 'lucide-react'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    alert("Thank you for your message! We'll get back to you soon.")
    setFormData({ name: '', email: '', message: '' })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-wag-primary/5 to-wag-secondary/5">
      <Header />

      <main className="container mx-auto px-4 pt-24 pb-16 md:pt-16 max-w-3xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold font-heading text-gray-900 mb-4">
            Contact
          </h1>
          <p className="text-lg text-gray-600">
            Help with Wagamour as a dog parent, or questions about the future
            provider program.
          </p>
        </div>

        {/* Provider inquiry → waitlist (not live services) */}
        <Card className="p-6 md:p-8 mb-10 border-purple-100 bg-gradient-to-br from-purple-50/80 to-white">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-purple-100 text-purple-700">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-1">
                Pet care providers · Coming soon
              </p>
              <h2 className="text-xl font-bold font-heading text-gray-900 mb-2">
                Provider or professional inquiry
              </h2>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">
                Booking pet care services through Wagamour is{' '}
                <strong>not</strong> available yet. We’re building a future
                provider layer; the only next step today is the waitlist on the
                homepage.
              </p>
              <Button
                asChild
                className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
              >
                <Link href="/#providers">
                  Join provider waitlist
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </Card>

        {/* Dog parents / general support */}
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Dog parents &amp; general support
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            For account help, feedback, or bugs, email us or use the form below.
          </p>
          <a
            href="mailto:hello@wagamour.com"
            className="inline-flex items-center gap-2 text-purple-600 font-medium hover:text-purple-700"
          >
            <Mail className="h-4 w-4" />
            hello@wagamour.com
          </a>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
        >
          <div>
            <label htmlFor="contact-name" className="sr-only">
              Your name
            </label>
            <input
              id="contact-name"
              type="text"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wag-primary focus:border-transparent"
              required
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="sr-only">
              Your email
            </label>
            <input
              id="contact-email"
              type="email"
              placeholder="Your email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wag-primary focus:border-transparent"
              required
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="sr-only">
              Your message
            </label>
            <textarea
              id="contact-message"
              placeholder="Your message"
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wag-primary focus:border-transparent"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-wag-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-wag-primary/90 transition-colors"
          >
            Send message
          </button>
        </form>
      </main>

      <Footer />
    </div>
  )
}
